import { BackendError, backendFetch, backendJson } from "@/lib/backend";
import type {
  RFIRequestListItem,
  RFIRequestSyncItem,
  RFIEmailTemplateResponse,
  RFISendEmailRequest,
  RFISendEmailResponse,
  CompanyAutocompleteResponse,
  SignedURLResponse,
} from "@/types/backend";

async function readBodyTextSafe(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return "";
  }
}

export async function getRfiDownloadUrl(
  projectId: string
): Promise<SignedURLResponse> {
  const { response, data } = await backendJson<SignedURLResponse>(
    `/api/projects/${projectId}/rfi/download-url`,
    { method: "GET" }
  );

  if (response.ok && data !== null) return data;

  const bodyText = await readBodyTextSafe(response);
  throw new BackendError("Failed to get RFI download URL.", {
    status: response.status,
    bodyText,
  });
}

export async function listRfiRequests(
  projectId: string
): Promise<RFIRequestListItem[]> {
  const { response, data } = await backendJson<RFIRequestListItem[]>(
    `/api/projects/${projectId}/rfi/requests`,
    { method: "GET" }
  );

  if (response.ok && data !== null) return data;

  const bodyText = await readBodyTextSafe(response);
  throw new BackendError("Failed to list RFI requests.", {
    status: response.status,
    bodyText,
  });
}

export async function syncRfiRequests(
  projectId: string,
  payload: RFIRequestSyncItem[]
): Promise<RFIRequestListItem[]> {
  const { response, data } = await backendJson<RFIRequestListItem[]>(
    `/api/projects/${projectId}/rfi/requests`,
    {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  if (response.ok && data !== null) return data;

  const bodyText = await readBodyTextSafe(response);
  throw new BackendError("Failed to sync RFI requests.", {
    status: response.status,
    bodyText,
  });
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  // filename*=UTF-8''...
  const mStar = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (mStar?.[1]) {
    try {
      return decodeURIComponent(mStar[1]);
    } catch {
      return mStar[1];
    }
  }
  // filename="..."
  const m = /filename\s*=\s*"([^"]+)"/i.exec(header) ?? /filename\s*=\s*([^;]+)/i.exec(header);
  if (!m?.[1]) return null;
  return m[1].trim();
}

export async function downloadRfiExcel(projectId: string): Promise<void> {
  const response = await backendFetch(`/api/projects/${projectId}/rfi/excel`, {
    method: "GET",
  });

  if (!response.ok) {
    const bodyText = await readBodyTextSafe(response);
    throw new BackendError("Failed to download RFI Excel.", {
      status: response.status,
      bodyText,
    });
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get("content-disposition");
  const filename =
    filenameFromContentDisposition(contentDisposition) ?? `rfi-${projectId}.xlsx`;

  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function getRfiEmailTemplate(
  projectId: string
): Promise<RFIEmailTemplateResponse> {
  const { response, data } = await backendJson<RFIEmailTemplateResponse>(
    `/api/projects/${projectId}/rfi/email-template`,
    { method: "GET" }
  );

  if (response.ok && data !== null) return data;

  const bodyText = await readBodyTextSafe(response);
  throw new BackendError("Failed to generate RFI email template.", {
    status: response.status,
    bodyText,
  });
}

export async function sendRfiEmail(
  projectId: string,
  payload: RFISendEmailRequest
): Promise<RFISendEmailResponse> {
  const { response, data } = await backendJson<RFISendEmailResponse>(
    `/api/projects/${projectId}/rfi/email/send`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  if (response.ok && data !== null) return data;

  const bodyText = await readBodyTextSafe(response);
  throw new BackendError("Failed to send RFI email.", {
    status: response.status,
    bodyText,
  });
}

export async function companyAutocomplete(
  q: string,
  signal?: AbortSignal
): Promise<CompanyAutocompleteResponse> {
  const qs = new URLSearchParams({ q }).toString();
  const { response, data } = await backendJson<CompanyAutocompleteResponse>(
    `/api/rfi/company-autocomplete?${qs}`,
    { method: "GET", signal }
  );

  if (response.ok && data !== null) return data;

  const bodyText = await readBodyTextSafe(response);
  throw new BackendError("Failed to autocomplete company names.", {
    status: response.status,
    bodyText,
  });
}

export async function industryAutocomplete(
  q: string,
  signal?: AbortSignal
): Promise<CompanyAutocompleteResponse> {
  const qs = new URLSearchParams({ q }).toString();
  const { response, data } = await backendJson<CompanyAutocompleteResponse>(
    `/api/rfi/industry-autocomplete?${qs}`,
    { method: "GET", signal }
  );

  if (response.ok && data !== null) return data;

  const bodyText = await readBodyTextSafe(response);
  throw new BackendError("Failed to autocomplete industries.", {
    status: response.status,
    bodyText,
  });
}


